"use client";

import clsx from "clsx";
import {AnimatePresence, motion} from "framer-motion";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import AskText from "@/components/ask/AskText";
import Icon from "@/components/library/Icon";

/** Reasoning blocks shorter than this AND under MIN_REASONING_SECONDS are hidden to reduce noise. */
export const MIN_REASONING_CHARS = 300;
export const MIN_REASONING_SECONDS = 5;

interface ReasoningBlockProps {
   content: string;
   isStreaming?: boolean;
}

const ReasoningBlock: React.FC<ReasoningBlockProps> = ({content, isStreaming = false}) => {
   const [isExpanded, setIsExpanded] = useState(false);
   const {t} = useTranslation("ask");
   const scrollRef = useRef<HTMLDivElement>(null);
   const startTimeRef = useRef<number | null>(null);
   const [durationSeconds, setDurationSeconds] = useState<number | undefined>(undefined);
   const [passedThreshold, setPassedThreshold] = useState(false);

   // Track streaming duration and decide whether to show
   useEffect(() => {
      if (isStreaming) {
         startTimeRef.current ??= Date.now();
      } else {
         if (startTimeRef.current !== null) {
            setDurationSeconds(Math.ceil((Date.now() - startTimeRef.current) / 1000));
            startTimeRef.current = null;
         }
         if (passedThreshold) {
            const timer = setTimeout(() => setIsExpanded(false), 2000);
            return () => clearTimeout(timer);
         }
      }
   }, [isStreaming, passedThreshold]);

   // During streaming, only show once a threshold is met (content long enough OR time elapsed)
   useEffect(() => {
      if (!isStreaming || passedThreshold) return;

      // Check content length threshold on every content update
      if (content.length >= MIN_REASONING_CHARS) {
         setPassedThreshold(true);
         setIsExpanded(true);
         return;
      }

      // Set a timer for the time threshold
      const elapsed = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
      const remaining = MIN_REASONING_SECONDS * 1000 - elapsed;
      if (remaining <= 0) {
         setPassedThreshold(true);
         setIsExpanded(true);
         return;
      }

      const timer = setTimeout(() => {
         setPassedThreshold(true);
         setIsExpanded(true);
      }, remaining);
      return () => clearTimeout(timer);
   }, [isStreaming, content, passedThreshold]);

   // Keep scroll pinned to bottom during streaming
   useEffect(() => {
      if (isStreaming && scrollRef.current) {
         scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
   }, [content, isStreaming]);

   if (!content.trim()) {
      return null;
   }

   // Hide trivial reasoning — never passed threshold during streaming
   if (!isStreaming && !passedThreshold) {
      return null;
   }

   // Still waiting for threshold during streaming — don't render yet
   if (isStreaming && !passedThreshold) {
      return null;
   }

   const getThinkingLabel = () => {
      if (isStreaming) return t("thinkingInProgress");
      if (durationSeconds !== undefined && durationSeconds > 0) {
         return t("thoughtForSeconds", {count: durationSeconds});
      }
      return t("thoughtForFewSeconds");
   };
   const thinkingLabel = getThinkingLabel();

   return (
      <motion.div
         initial={{opacity: 0, height: 0}}
         animate={{opacity: 1, height: "auto"}}
         transition={{duration: 0.25, ease: [0.16, 1, 0.3, 1]}}
         className="mb-4 overflow-hidden"
      >
         <button
            onClick={() => setIsExpanded((prev) => !prev)}
            className="flex items-center gap-1 text-xs text-400 hover:text-600 transition-colors py-1"
         >
            <Icon icon="chevron-right" className={clsx(
               "w-3 h-3 transition-transform duration-200",
               isExpanded ? "rotate-90" : "rotate-0"
            )} />
            <span className={clsx(isStreaming && "animate-pulse")}>
               {thinkingLabel}
            </span>
         </button>
         <AnimatePresence initial={false}>
            {isExpanded && (
               <motion.div
                  key="thinking-content"
                  initial={{opacity: 0, height: 0}}
                  animate={{opacity: 1, height: "auto"}}
                  exit={{opacity: 0, height: 0}}
                  transition={{duration: 0.2, ease: [0.16, 1, 0.3, 1]}}
                  className="overflow-hidden"
               >
                  <div
                     ref={scrollRef}
                     className={clsx(
                        "mt-1.5 ml-[7px] pl-3 border-l-2 border-primary/30",
                        "text-xs text-500 overflow-y-auto",
                        isStreaming ? "max-h-[150px]" : "max-h-[200px]"
                     )}
                  >
                     <div className="[&_p]:my-1 [&_strong]:text-600 [&_strong]:font-medium">
                        <AskText text={content} isMarkDown variant="v1" />
                     </div>
                     {isStreaming && (
                        <span className="inline-block w-1 h-3 bg-400 animate-pulse ml-0.5 align-text-bottom" />
                     )}
                  </div>
               </motion.div>
            )}
         </AnimatePresence>
      </motion.div>
   );
};

export default ReasoningBlock;
